/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { JSX } from 'preact';
import type { ForwardedRef } from 'preact/compat';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type {
  Cell,
  CellOverride,
  Column,
  ColumnWidths,
  CurrentRowVariant,
  Props,
  TableViewHandle,
  TableViewportConfig
} from '#UNSAFE_TableView';
import type { TableVariantOptions } from './themes/TableStyles.css';

import { createRef } from 'preact';
import { forwardRef } from 'preact/compat';
import { useCallback, useState } from 'preact/hooks';
import { useCollectionInteractionContext } from '#hooks/UNSAFE_useCollectionInteractionContext';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useId } from '#hooks/UNSAFE_useId';
import { TabbableModeContext } from '#hooks/UNSAFE_useTabbableMode';
import { useTestId } from '#hooks/UNSAFE_useTestId';
import { useUser } from '#hooks/UNSAFE_useUser';
import { classNames } from '#utils/UNSAFE_classNames';
import { FocusTracker } from '#PRIVATE_FocusTracker';
import { useDataManager } from '#PRIVATE_TableCommon/hooks/useDataManager';
import { useInteractionManager } from './hooks/useInteractionManager';
import { useSizingManager } from './hooks/useSizingManager';
import { innerTableStyles, scrollerStyles, userSelectStyles } from './themes/Table.css';
import { tableSkeletonStyles, tableSkeletonStylesHC } from './themes/TableSkeletonStyles.css';
import { TableRedwoodTheme } from './themes/redwood/TableTheme';
import { TableBody } from './TableBody';
import { TableColGroup } from './TableColGroup';
import { TableContextMenu, TableResizeColumnDialog } from './TableContextMenu';
import { TableDragIndicator } from './TableDragIndicator';
import { TableFooter } from './TableFooter';
import { TableHeader } from './TableHeader';
import { TableSkeletonRow } from './TableSkeletonRow';
import { getClientHints } from '#utils/PRIVATE_clientHints';

export type ActiveCell<K, C> =
  | Cell<K, C>
  | {
      rowKey?: never;
      columnKey: C;
      type: 'loading';
    }
  | {
      rowKey?: never;
      columnKey: C;
      type: 'loadMore';
    }
  | {
      rowKey?: never;
      columnKey?: never;
      type: 'pending';
    };

export type ActiveCellDetail<K, C> = {
  value?: ActiveCell<K, C>;
};

export type TableColumn<K, D, C> = {
  key: C;
  value: Column<K, D, C>;
};

export type BorderEdges = {
  hasStart: boolean;
  hasEnd: boolean;
  width: number;
  offset: number;
}[];

export type AppliedColumnWidths<C extends string> = {
  netColumnWidths: ColumnWidths<C>;
  fullColumnWidths: number[];
  realColumnWidths: number[];
  borderEdges: BorderEdges;
  borderOffset: number;
  totalBorderOffset: number;
};

export type StickyColumnInfo = {
  stickyColumns: number[];
  firstStickyColumnIndex?: number;
  finalStickyColumnIndex?: number;
};

export type ColumnHeaderFooterInfo<C> = {
  key: C;
  isHeader: boolean;
};

export type CurrencyStatus<K, D, C> = {
  activeCell?: ActiveCell<K, C>;
  currentCell?: Cell<K, C>;
  currentCellOverride?: CellOverride<K, C>;
  hasPendingCurrentCellOverride: boolean;
  hasPendingCurrentCellOverrideScroll: boolean;
  previousEmbeddedSelectCurrentCell?: Cell<K, C>;
  loadMoreInitialData?: CollectionData<K, D>[];
};

export type SampledWidths<C extends string> = {
  minWidths: ColumnWidths<C>;
  weightWidths: ColumnWidths<C>;
};

export type ViewportInfo = {
  isExternalScroller: boolean;
  viewportConfig: TableViewportConfig;
};

export type OverflowInfo = {
  isHorizontal: boolean;
  isVertical: boolean;
};

export type SizingInfo =
  | {
      isInitialRender: true;
      boxWidth?: number;
      contentWidth?: number;
      boxHeight?: number;
      contentHeight?: number;
      isScrollbarSizeKnown: false;
      defaultScrollbarWidth?: number;
      defaultScrollbarHeight?: number;
    }
  | {
      isInitialRender: false;
      boxWidth: number;
      contentWidth: number;
      boxHeight: number;
      contentHeight: number;
      isScrollbarSizeKnown: true;
      defaultScrollbarWidth: number;
      defaultScrollbarHeight: number;
    }
  | {
      isInitialRender: false;
      boxWidth: number;
      contentWidth: number;
      boxHeight: number;
      contentHeight: number;
      isScrollbarSizeKnown: false;
      defaultScrollbarWidth?: number;
      defaultScrollbarHeight?: number;
    };

export type ColumnResizeInfo<C> =
  | {
      isResizing: false;
      isResizeHover: boolean;
      key?: C;
      startX?: never;
      delta?: never;
      ignoreClick: boolean;
    }
  | {
      isResizing: true;
      isResizeHover: boolean;
      key: C;
      startX: number;
      delta: number;
      ignoreClick: boolean;
    };

export type ScrollDetail<K, C> = {
  cell: ActiveCell<K, C> | Cell<K, C>;
  locationX?: 'inView' | 'start' | 'end';
  locationY?: 'inView' | 'top' | 'bottom';
};

const _defaultSelected = {
  row: { all: false, keys: new Set() },
  column: { all: false, keys: new Set() }
};

const _disabledHeaderFooterInteractionKeys: ('enter' | 'space')[] = ['enter'];

/**
 * Component that renders data in a flat table.
 */
export const Table = forwardRef(function Table<K extends string | number, D, C extends string>(
  {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    getAccessibleRowHeaders,
    columns,
    columnOrder,
    columnWidths,
    contextMenuConfig,
    viewportConfig,
    layout = 'contents',
    data,
    getRowKey,
    hasMore = false,
    onLoadMore = () => {},
    currentCellOverride,
    onPersistCurrentCell,
    currentRowVariant,
    gridlines,
    selectionMode,
    selected = _defaultSelected as NonNullable<Props<K, D, C>['selected']>,
    onSelectionChange,
    onRowAction,
    sortCriterion,
    onSortCriterionChange,
    horizontalScrollPositionOverride,
    verticalScrollPositionOverride,
    columnResizingIndicator,
    onColumnResizing,
    onColumnResize,
    noDataRenderer,
    scrollerRef,
    testId
  }: Props<K, D, C>,
  ref?: ForwardedRef<TableViewHandle<K, C>>
) {
  const tableId = useId();
  const testIdProps = useTestId(testId);

  // rootRef is callback-based to meet useResizeObserver hook's state management requirements
  const [rootRef, setRootRef] = useState(createRef<HTMLDivElement>());
  const rootRefCallback = useCallback((node: HTMLDivElement | null) => {
    setRootRef({ current: node });
  }, []);
  // innerTableRef is callback-based to meet useResizeObserver hook's state management requirements
  const [innerTableRef, setInnerTableRef] = useState(createRef<HTMLTableElement>());
  const innerTableRefCallback = useCallback((node: HTMLTableElement | null) => {
    setInnerTableRef({ current: node });
  }, []);

  const { direction, forcedColors } = useUser();
  const isHighContrast = forcedColors === 'active';
  const isRtl = direction === 'rtl';

  const isEmbedded = useCollectionInteractionContext() === 'embedded';
  const rowVariant: CurrentRowVariant = currentRowVariant ?? isEmbedded ? 'highlight' : 'none';

  const { managedData: tableData, onManagedLoadMore: onTableLoadMore } = useDataManager<K, D>({
    data,
    getRowKey,
    hasMore,
    onLoadMore
  });

  const {
    columnBorderEdges,
    isLayoutRequired,
    viewportInfo,
    columnsArray,
    appliedColumnWidths,
    sizingInfoRef,
    overflowInfoRef,
    innerTableHeight,
    hasVerticalUnderflow,
    stickyEdgesState,
    setStickyEdgesState,
    stickyColumnInfo,
    scrollerRefCallback
  } = useSizingManager<K, D, C>({
    rootRef,
    innerTableRef,
    tableId,
    tableData,
    viewportConfig,
    externalScrollerAccessRef: scrollerRef,
    layout,
    columns,
    columnOrder,
    columnWidths,
    isRtl,
    isHighContrast
  });

  const cellHasRenderer = useCallback(
    (cell: ActiveCell<K, C>) => {
      let hasRenderer = false;
      if (cell.type === 'noData') {
        hasRenderer = noDataRenderer != null;
      } else if (cell.type !== 'loadMore' && cell.type !== 'loading' && cell.type !== 'pending') {
        for (const column of columnsArray) {
          if (column.key === cell.columnKey) {
            if (cell.type === 'data') {
              hasRenderer = column.value.renderer != null;
            } else if (cell.type === 'header') {
              hasRenderer = column.value.headerRenderer != null;
            } else if (cell.type === 'footer') {
              hasRenderer = column.value.footerRenderer != null;
            }
            break;
          }
        }
      }
      return hasRenderer;
    },
    [columnsArray, noDataRenderer]
  );

  const isPendingLayout = layout === 'pending';

  // aria-rowcount includes header and footer rows
  const hasFooters = columnsArray.some((column: TableColumn<K, D, C>) => {
    return column.value.footerRenderer != null || column.value.footerText != null;
  });
  const ariaRowCount = hasMore || isPendingLayout ? -1 : tableData.length + (hasFooters ? 2 : 1);
  const ariaColumnCount = Math.max(columnsArray.length, 1);

  const {
    interactionManagerProps,
    scrollHandlingProps,
    onTableSelectionChange,
    isShowFocusRing,
    isTabbableMode,
    currentCell,
    currentActiveCell,
    resizeInfoRef,
    onStartFocusTracking,
    onFocusStartEdge,
    onFocusEndEdge,
    activeStyleColumnInfo,
    hoverStyleColumnInfo,
    tooltipContent,
    contextMenuContext,
    menuProps,
    isResizeDialogOpen,
    setIsResizeDialogOpen,
    disabledRowInteractionKeys
  } = useInteractionManager<K, D, C>({
    rootRef,
    viewportInfo,
    ref,
    tableId,
    tableData,
    hasMore,
    hasFooters,
    isRtl,
    columnsArray,
    stickyColumnInfo,
    appliedColumnWidths,
    isLayoutRequired,
    sizingInfoRef,
    overflowInfoRef,
    stickyEdgesState,
    setStickyEdgesState,
    cellHasRenderer,
    horizontalScrollPositionOverride,
    verticalScrollPositionOverride,
    onColumnResizing,
    onColumnResize,
    currentCellOverride,
    onPersistCurrentCell,
    selectionMode,
    selected,
    onSelectionChange,
    onRowAction,
    sortCriterion,
    onSortCriterionChange,
    isPendingLayout,
    isEmbedded,
    contextMenuConfig
  });

  // initialize base Table style classes
  const { classes: themeClasses } = useComponentTheme<TableVariantOptions>(TableRedwoodTheme, {
    pendingLayout: isPendingLayout ? 'isPendingLayout' : 'notPendingLayout',
    showFocusRing: isShowFocusRing ? 'isShowFocusRing' : 'notShowFocusRing',
    externalScroller: viewportInfo.isExternalScroller ? 'isExternalScroller' : 'notExternalScroller'
  });

  let scrollerStyle;
  let scrollerPadding = '';
  const scrollerClassArray = [scrollerStyles.base];
  if (viewportInfo.isExternalScroller) {
    scrollerClassArray.push(scrollerStyles.externalScroller);
  } else if (!sizingInfoRef.current.isScrollbarSizeKnown) {
    scrollerClassArray.push(scrollerStyles.forceScroll);
  } else {
    if (overflowInfoRef.current.isHorizontal) {
      scrollerClassArray.push(scrollerStyles.horizontalScroll);
    } else {
      scrollerClassArray.push(scrollerStyles.noHorizontalScroll);
    }
    if (overflowInfoRef.current.isVertical) {
      scrollerClassArray.push(scrollerStyles.verticalScroll);
    } else {
      scrollerClassArray.push(scrollerStyles.noVerticalScroll);
      scrollerPadding = `padding-inline-end: ${sizingInfoRef.current.defaultScrollbarWidth}px;`;
    }
  }
  // hide the table contents when layout is required and otherwise valid
  if (isLayoutRequired && columnsArray.length > 0) {
    scrollerStyle = `${scrollerPadding}opacity:0.001;`;
  } else {
    scrollerStyle = scrollerPadding;
  }

  const scrollerClasses = classNames(scrollerClassArray);
  const allowBrowserContextMenu = !(
    getClientHints().platform === 'ios' || getClientHints().platform === 'android'
  );

  const classArray = [themeClasses];
  if (!allowBrowserContextMenu) {
    classArray.push(userSelectStyles.userSelect);
  }

  const classes = classNames(classArray);
  const tableClasses = classNames([innerTableStyles.base]);

  const startStickyEdge = stickyEdgesState.start;
  const endStickyEdge = stickyEdgesState.end;

  const hasVerticalGridlines = gridlines?.vertical === 'visible';
  const hasHorizontalGridlines = gridlines?.horizontal !== 'hidden';

  const isRowSelectionEnabled =
    selectionMode?.row === 'single' ||
    selectionMode?.row === 'multiple' ||
    selectionMode?.row === 'multipleToggle';
  const isColumnSelectionEnabled =
    selectionMode?.column === 'single' || selectionMode?.column === 'multiple';
  const supportsMultipleSelection =
    selectionMode?.column === 'multiple' ||
    selectionMode?.row === 'multiple' ||
    selectionMode?.row === 'multipleToggle';

  const getTableBodyProps = () => {
    return {
      rootRef,
      viewportInfo,
      tableId,
      isValidRender: columnsArray.length > 0,
      tableData,
      columnsArray,
      getAccessibleRowHeaders,
      hasVerticalGridlines,
      hasHorizontalGridlines,
      hasFooters,
      hasVerticalUnderflow,
      selected,
      isRowSelectionEnabled,
      isSingleRowSelectionEnabled: selectionMode?.row === 'single',
      activeCell: currentActiveCell,
      currentRowKey: currentCell?.type === 'data' ? currentCell.rowKey : undefined,
      currentRowVariant: rowVariant,
      isShowFocusRing,
      isTabbableMode,
      hasMore,
      onLoadMore: onTableLoadMore,
      noDataRenderer,
      disabledInteractionKeys: disabledRowInteractionKeys,
      onSelectionChange: onTableSelectionChange,
      startStickyEdge,
      endStickyEdge,
      firstStickyColumnIndex: stickyColumnInfo.firstStickyColumnIndex,
      finalStickyColumnIndex: stickyColumnInfo.finalStickyColumnIndex,
      columnBorderEdges,
      isRtl,
      isEmbedded,
      isHighContrast
    };
  };

  const getTableColGroupProps = () => {
    let columnWidthsArray;
    if (!isLayoutRequired && appliedColumnWidths?.realColumnWidths != null) {
      columnWidthsArray = appliedColumnWidths.realColumnWidths;
    } else if (layout === 'fixed') {
      // default to 100px for each column when layout is not yet available
      columnWidthsArray = [];
      for (let i = 0; i < columnsArray.length; i++) {
        columnWidthsArray[i] = 100;
      }
    } else {
      columnWidthsArray = [];
      for (let i = 0; i < columnsArray.length; i++) {
        columnWidthsArray[i] = undefined;
      }
    }
    return {
      tableId,
      isRendered: columnsArray.length > 0,
      columnWidthsArray
    };
  };

  const getTableHeaderProps = () => {
    return {
      tableId,
      isRendered: columnsArray.length > 0,
      columnsArray,
      hasVerticalGridlines,
      activeKey: currentActiveCell?.type === 'header' ? currentActiveCell.columnKey : undefined,
      isTabbableMode,
      selectedColumnKeys: selected.column,
      selectedRowKeys: selected.row,
      isColumnSelectionEnabled,
      isRowSelectionEnabled,
      disabledInteractionKeys: _disabledHeaderFooterInteractionKeys,
      onSelectionChange: onTableSelectionChange,
      isForceSortIcons: layout === 'contents' && isLayoutRequired,
      activeStyleKey: activeStyleColumnInfo?.isHeader ? activeStyleColumnInfo?.key : undefined,
      hoverStyleKey: hoverStyleColumnInfo?.isHeader ? hoverStyleColumnInfo?.key : undefined,
      sortCriterion,
      onSortCriterionChange,
      isShowFocusRing,
      stickyTopOffset: viewportInfo.viewportConfig.scrollerOffsetTop,
      startStickyEdge,
      endStickyEdge,
      firstStickyColumnIndex: stickyColumnInfo.firstStickyColumnIndex,
      finalStickyColumnIndex: stickyColumnInfo.finalStickyColumnIndex,
      columnBorderEdges,
      isResizeHover: resizeInfoRef.current.isResizeHover,
      isRtl,
      isHighContrast
    };
  };

  const getTableFooterProps = () => {
    return {
      tableId,
      isRendered: hasFooters,
      columnsArray,
      hasVerticalGridlines,
      activeKey: currentActiveCell?.type === 'footer' ? currentActiveCell.columnKey : undefined,
      isTabbableMode,
      selectedColumnKeys: selected.column,
      isColumnSelectionEnabled,
      activeStyleKey: !activeStyleColumnInfo?.isHeader ? activeStyleColumnInfo?.key : undefined,
      hoverStyleKey: !hoverStyleColumnInfo?.isHeader ? hoverStyleColumnInfo?.key : undefined,
      isShowFocusRing,
      disabledInteractionKeys: _disabledHeaderFooterInteractionKeys,
      stickyBottomOffset: viewportInfo.viewportConfig.scrollerOffsetBottom,
      startStickyEdge,
      endStickyEdge,
      firstStickyColumnIndex: stickyColumnInfo.firstStickyColumnIndex,
      finalStickyColumnIndex: stickyColumnInfo.finalStickyColumnIndex,
      columnBorderEdges,
      dataRowCount: tableData.length,
      isRtl,
      isHighContrast
    };
  };

  const getTableDragIndicator = () => {
    if (
      columnResizingIndicator != null &&
      !isLayoutRequired &&
      appliedColumnWidths?.fullColumnWidths != null
    ) {
      let position = 0;
      for (let i = 0; i < columnsArray.length; i++) {
        position += appliedColumnWidths.fullColumnWidths[i];
        if (columnsArray[i].key === columnResizingIndicator.key) {
          if (columnResizingIndicator.delta != null) {
            position += columnResizingIndicator.delta;
          }
          break;
        }
      }
      return <TableDragIndicator position={position} isRtl={isRtl} height={innerTableHeight} />;
    }
    return undefined;
  };

  const getTableStyle = () => {
    if (!isLayoutRequired && appliedColumnWidths?.realColumnWidths != null) {
      let totalWidth = 0;
      for (let i = 0; i < appliedColumnWidths.realColumnWidths.length; i++) {
        totalWidth += appliedColumnWidths.realColumnWidths[i];
      }
      return `width:${totalWidth}px;table-layout:fixed;`;
    } else if (columnsArray.length > 0) {
      if (layout === 'fixed') {
        // default to 100px for each column when layout is not yet available
        return `width:${columnsArray.length * 100}px;table-layout:fixed;`;
      } else {
        // shrink table down as much as possible when a 'contents' layout is required, otherwise
        // the sampled column widths will not reflect the true weight sizes with line-clamping
        return 'width:max-content;will-change:width;';
      }
    }
    // no columns are present
    return 'width:100%;';
  };

  const skeletonStyles = isHighContrast ? tableSkeletonStylesHC : tableSkeletonStyles;
  const getPendingLayoutSkeletons = () => {
    return isPendingLayout ? (
      <div class={skeletonStyles.pendingLayout}>
        <table role={'presentation'} style={'width:100%;table-layout:fixed;'}>
          <tbody role={'presentation'}>
            <TableSkeletonRow
              tableId={tableId}
              hasTabIndex={currentActiveCell?.type === 'pending'}
              colspan={ariaColumnCount}
              isPendingLayout={true}
              isShowFocusRing={false}
              isEmbedded={isEmbedded}
              isHighContrast={isHighContrast}
            />
          </tbody>
        </table>
      </div>
    ) : undefined;
  };

  const ariaMultiSelectableProps = supportsMultipleSelection
    ? { 'aria-multiselectable': true }
    : isRowSelectionEnabled || isColumnSelectionEnabled
    ? { 'aria-multiselectable': false }
    : {};

  return (
    <>
      <div
        {...testIdProps}
        ref={rootRefCallback}
        aria-colcount={ariaColumnCount}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-rowcount={ariaRowCount}
        {...ariaMultiSelectableProps}
        class={classes}
        id={tableId}
        role={'grid'}
        tabIndex={currentActiveCell != null ? -1 : 0}
        {...interactionManagerProps}>
        <div
          ref={scrollerRefCallback}
          class={scrollerClasses}
          role={'rowgroup'}
          style={scrollerStyle}
          tabIndex={-1}
          {...scrollHandlingProps}>
          <FocusTracker
            isDisabled={!isTabbableMode}
            onFocusStartEdge={onFocusStartEdge}
            onFocusEndEdge={onFocusEndEdge}
            onStartTracking={onStartFocusTracking}>
            <TabbableModeContext.Provider value={{ isTabbable: isTabbableMode }}>
              <table
                ref={innerTableRefCallback}
                aria-hidden={isPendingLayout ? true : undefined}
                class={tableClasses}
                role={'presentation'}
                style={getTableStyle()}
                data-oj-table-inner-table={tableId}>
                <TableColGroup {...getTableColGroupProps()} />
                <TableHeader {...getTableHeaderProps()} />
                <TableBody {...getTableBodyProps()} />
                <TableFooter {...getTableFooterProps()} />
              </table>
            </TabbableModeContext.Provider>
            {getTableDragIndicator()}
          </FocusTracker>
          {tooltipContent}
        </div>
        {getPendingLayoutSkeletons()}
      </div>
      {contextMenuConfig != null && contextMenuContext != null && (
        <>
          <TableContextMenu
            {...menuProps}
            contextMenuConfig={contextMenuConfig}
            contextMenuContext={contextMenuContext}
          />
          {isResizeDialogOpen &&
          currentActiveCell?.columnKey != null &&
          appliedColumnWidths != null ? (
            <TableResizeColumnDialog
              isOpen={isResizeDialogOpen}
              columnKey={currentActiveCell.columnKey}
              appliedColumnWidths={appliedColumnWidths}
              onColumnResize={onColumnResize}
              setIsResizeDialogOpen={setIsResizeDialogOpen}
            />
          ) : undefined}
        </>
      )}
    </>
  );
}) as <K extends string | number, D, C extends string>(
  p: Props<K, D, C> & { ref?: ForwardedRef<TableViewHandle<K, C>> }
) => JSX.Element;
